<template>
  <tr>
    <td>
      <button type="button" class="user-row__link" @click="showTransactions(user.userId, null)">
        <span class="user-row__email">{{ user.email }}</span>
        <span class="user-row__id">#{{ user.userId }}</span>
      </button>
    </td>
    <td>
      <span class="user-row__app">{{ getAppById(user.appId) }}</span>
      <span class="text-muted small"> {{ getPlatformById(user.platformId) }}</span>
    </td>
    <td>
      <button type="button" class="user-row__link" @click="showTransactions(user.userId, user.installationId)">
        <span v-if="user.shipName">{{ user.shipName }}</span>
        <span v-else class="text-muted">{{ user.deviceId }}</span>
      </button>
    </td>
    <td class="text-nowrap text-muted small">
      <my-date :dateTime="user.createdAt" />
    </td>
    <td>
      <div class="user-row__price">
        <input
          class="form-control form-control-sm user-row__price-input"
          type="number"
          :disabled="disabled || (!isEditing && editLocked)"
          v-model.number="tempPrice"
          @focus="startEdit"
        />
        <template v-if="isEditing">
          <button type="button" class="btn btn-sm btn-primary" @click="save">Save</button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="cancelEdit">Cancel</button>
        </template>
      </div>
    </td>
    <td>
      <div v-if="!moveOpen" class="user-row__move-idle">
        <button type="button" class="user-row__move-trigger" @click="startMove">
          Move
        </button>
      </div>
      <div v-else class="user-row__move">
        <select class="form-select form-select-sm" v-model.number="targetAgreementId">
          <option disabled :value="null">Select agreement…</option>
          <option v-for="a in agreements" :key="a.id" :value="a.id">
            [{{ a.id }}] {{ a.name }}
          </option>
        </select>
        <button
          class="btn btn-sm btn-primary"
          type="button"
          :disabled="!targetAgreementId || moving"
          @click="confirmMove"
        >
          {{ moving ? '…' : 'Confirm' }}
        </button>
        <button
          class="btn btn-sm btn-outline-secondary"
          type="button"
          :disabled="moving"
          @click="cancelMove"
        >
          Cancel
        </button>
      </div>
    </td>
  </tr>
</template>

<script>
import MyDate from '@/components/MyDate.vue';

export default {
  name: 'AgreementUserRow',
  components: { MyDate },
  props: {
    user: Object,
    agreementId: [String, Number],
    agreements: { type: Array, default: () => [] },
    editLocked: Boolean,
    disabled: Boolean,
    moveOpen: Boolean
  },
  data() {
    return {
      isEditing: false,
      tempPrice: this.user.autoApproveByDistributorOrderMaxPrice ?? null,
      originalPrice: this.user.autoApproveByDistributorOrderMaxPrice ?? null,
      targetAgreementId: null,
      moving: false
    };
  },
  watch: {
    moveOpen(open) {
      if (!open) {
        this.targetAgreementId = null;
        this.moving = false;
      }
    }
  },
  methods: {
    getAppById(id) {
      const map = {
        0: 'mKart', 1: 'NjordPilot', 2: 'Vega', 10: 'dKart',
        11: 'ECDIS', 101: 'mKartEcdis', 102: 'dKartNavigator', 105: 'dKartFishing'
      };
      return map[id] ?? 'n/a';
    },
    getPlatformById(id) {
      const map = { 1: 'WinPC', 2: 'WinCE', 3: 'Android', 4: 'iOS' };
      return map[id] ?? 'n/a';
    },
    showTransactions(userId, instId) {
      this.$router.push({ name: 'transactions', params: { agreementId: this.agreementId, userId, instId } });
    },
    startEdit() {
      if (!this.isEditing) {
        this.$emit('edit-lock', this.user.installationId);
        this.isEditing = true;
      }
    },
    save() {
      this.$emit('save', this.user.userAgreementId, this.tempPrice);
      this.originalPrice = this.tempPrice;
      this.isEditing = false;
      this.$emit('edit-lock', null);
    },
    cancelEdit() {
      this.tempPrice = this.originalPrice;
      this.isEditing = false;
      this.$emit('edit-lock', null);
    },
    startMove() {
      this.targetAgreementId = null;
      this.$emit('move-open', this.user.userAgreementId);
    },
    cancelMove() {
      this.targetAgreementId = null;
      this.$emit('move-open', null);
    },
    confirmMove() {
      if (!this.targetAgreementId) return;
      const target = this.agreements.find(a => a.id === this.targetAgreementId);
      const label = target ? `[${target.id}] ${target.name}` : this.targetAgreementId;
      const ok = confirm(
        `Bind this installation only to agreement ${label}? It will be removed from all other agreements. Existing orders and transactions stay on their previous agreements.`
      );
      if (!ok) return;
      this.moving = true;
      this.$emit('move', this.user.userAgreementId, this.targetAgreementId, () => {
        this.moving = false;
        this.targetAgreementId = null;
      });
    }
  }
};
</script>

<style scoped>
.user-row__link {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  padding: 0;
  border: none;
  background: none;
  color: #0d6efd;
  text-align: left;
  cursor: pointer;
}

.user-row__link:hover .user-row__email,
.user-row__link:hover {
  text-decoration: underline;
}

.user-row__email {
  font-weight: 500;
}

.user-row__id {
  font-size: 0.75rem;
  color: #6c757d;
}

.user-row__app {
  font-weight: 500;
}

.user-row__price {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.user-row__price-input {
  width: 6.5rem;
}

.user-row__move-trigger {
  padding: 0;
  border: none;
  background: none;
  color: #0d6efd;
  font-size: 0.875rem;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.user-row__move-trigger:hover {
  color: #0a58ca;
}

.user-row__move {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  min-width: 14rem;
}

.user-row__move .form-select {
  min-width: 10rem;
  max-width: 16rem;
}
</style>
